/**
 * The backend's vocabulary, in English.
 *
 * The API speaks in SCREAMING_SNAKE enums, snake_case field names and UUIDs,
 * because it is a contract between machines. Rendering that verbatim asks an
 * operator to learn the schema in order to read their own data - and worse,
 * it reads as unfinished. Everything an operator sees passes through here.
 *
 * Where a term has no plain equivalent, the sentence explains it rather than
 * inventing a friendlier word that means something subtly different: the
 * meaning is never softened, only stated.
 */

/** Why an object could not be attributed to a tool. */
const STRAY_REASON: Record<string, string> = {
  UNRECOGNIZED_SCHEMA: 'read, but matched no known tool',
  ZERO_BYTE: 'empty file',
  CONTRADICTED_CANDIDATES: 'two rules disagreed, so neither was trusted',
  DETECTION_NOT_SUPPORTED: 'the tool is not yet proven by enough evidence',
  READ_BUDGET_EXCEEDED: 'too large to read within the budget',
  DELETE_MARKER: 'deleted at source',
};

/** What the gap register found. */
const GAP_CLASS: Record<string, string> = {
  MISSING: 'missing',
  INACCESSIBLE: 'could not be opened',
  CORRUPT: 'corrupt',
  TRUNCATED: 'cut short',
  UNSUPPORTED_FORMAT: 'format not supported',
  PARSER_REJECTED: 'rejected by the parser',
  ORPHANED: 'orphaned',
  BROKEN_REFERENCE: 'points at something absent',
  SCHEMA_DRIFT: 'shape changed mid-export',
  NOT_EXPORTABLE: 'the tool does not export it',
  UNKNOWN_SCOPE: 'scope unproven',
  EVIDENCE_INSUFFICIENT: 'not enough evidence to claim a tool',
  STALE: 'out of date',
  LIVENESS_FAILED: 'source no longer reachable',
  CONTRADICTION: 'evidence disagreed',
  NOT_APPLICABLE: 'not applicable',
};

/** Where a package has got to. */
const PACKAGING_STATE: Record<string, string> = {
  DISCOVERING: 'choosing what ships',
  AXIS_PENDING: 'choosing the layout',
  STRUCTURING: 'building the structure',
  NAMING: 'naming',
  DOCUMENTING: 'documents awaiting approval',
  EXPORTABLE: 'ready to export',
  GATE_PENDING: 'checks passed, awaiting release',
  GATE_PASSED: 'released',
  GATE_FAILED: 'refused by the checks',
  DELIVERING: 'delivering',
  DELIVERED: 'delivered',
  DELIVERY_FAILED: 'delivery failed',
};

/**
 * Why a section of an analysis report has no value in it.
 *
 * The report has three substantive sections and on every run to date all
 * three come back UNAVAILABLE with one of these reasons. The page showed
 * none of them and called the report "its durable result", so five green
 * FINISHED runs read as five successes that produced something.
 */
const REPORT_UNAVAILABLE: Record<string, string> = {
  NO_RUN_SCOPED_ATTRIBUTION_BINDING:
    'This run did not attribute any files itself. Attribution happens per bucket, in the walk, and is shown on the pipeline.',
  NO_STRUCTURED_RECONSTRUCTION_PUBLICATION:
    'No records were reconstructed. The tables for them exist and nothing writes to them yet, which is the one gap the rest of the product waits on.',
  NO_COMMERCIAL_VERDICT_PUBLICATION:
    'No commercial verdict was published. A verdict needs reconstructed records to judge, so it cannot be reached before they exist.',
};

/**
 * Why a section of a walkthrough cannot be opened.
 *
 * These are the backend's own reason codes and they reached the screen
 * through the generic fallback, so a buyer read "no validated preview
 * adapter" on the surface built to show them a corpus. An adapter is
 * machinery; what it means to them is that nothing here can read that kind of
 * record yet.
 */
/**
 * A delivery's own states, which are not the packaging run's.
 *
 * These arrived as untyped strings for as long as the route answered an
 * untyped envelope, and `say()` rendered them by lowercasing - which is how
 * `CREATING_BUCKET` would have reached an operator as "creating bucket" with
 * no hint that the bucket being created is a NEW one beside the source. The
 * enum is real now, so these are written rather than derived.
 */
const DELIVERY_STATE: Record<string, string> = {
  // QUEUED is deliberately absent: it already means "waiting to start" for a
  // run, and one token carrying two words across surfaces is the thing the
  // collision guard exists to stop.
  CREATING_BUCKET: 'creating its own destination bucket',
  UPLOADING: 'uploading',
  VERIFYING: 'reading every object back to check it',
  DELIVERED: 'delivered',
  FAILED: 'failed',
};

/**
 * What became of a reconstruction that was asked for.
 *
 * REFUSED is not a lesser FAILED: no run was ever opened, so there is nothing
 * to inspect, and the two must not read alike.
 */
const RECONSTRUCTION_REQUEST_STATE: Record<string, string> = {
  REQUESTED: 'asked for, not yet claimed',
  DISPATCHED: 'running',
  SUCCEEDED: 'finished',
  FAILED: 'failed',
  REFUSED: 'refused before any run was opened',
  DISPATCH_FAILED: 'never started',
};

const SECTION_REASON: Record<string, string> = {
  NO_VALIDATED_PREVIEW_ADAPTER: 'nothing here can read this kind of record yet',
  NO_REGISTERED_ADAPTER: 'nothing here can read this kind of record yet',
  NO_PUBLISHED_RECORDS: 'no record of this kind has been reconstructed yet',
  NOT_APPLICABLE_FOR_TOOL: 'this tool does not produce records of this kind',
};

/** Run and job states shared across analysis, sync and walks. */
const RUN_STATE: Record<string, string> = {
  QUEUED: 'waiting to start',
  RUNNING: 'running',
  SUCCEEDED: 'finished',
  FAILED: 'failed',
  COMPLETE: 'complete',
  PENDING: 'pending',
  REGISTERED: 'registered',
  LISTING: 'listing files',
  DIFFING: 'comparing',
  PROBING: 'checking access',
  ACCESS_BLOCKED: 'access blocked',
  AVAILABLE: 'available',
  UNAVAILABLE: 'not available',
  EMPTY: 'nothing to show',
  UNSUPPORTED: 'not supported here',
  SUPPORTED: 'available',
  EVIDENCE_BACKED: 'proven by evidence',
  CANDIDATE: 'awaiting evidence',
  CONTRADICTED: 'evidence disagreed',
  INSUFFICIENT: 'not enough evidence',
  SUPERSEDED: 'replaced by a later one',
  NOT_STARTED: 'not started',
  SAMPLED: 'partly listed',
  DRAFTED: 'drafted',
  ACCEPTED: 'accepted',
};

/**
 * How far the listing of a bucket got.
 *
 * These reach a stamp on the scope list and the walkthrough header, where the
 * raw enum read as though the product were speaking to itself. "Manifest
 * reconciled" in particular is a real distinction, the listing having been
 * checked against the run manifest, and as two capitalised words it said
 * nothing.
 */
const INVENTORY_COVERAGE: Record<string, string> = {
  ACCESSIBLE_OBJECTS_ENUMERATED: 'every readable file listed',
  OBJECT_VERSIONS_ENUMERATED: 'every version of every file listed',
  MANIFEST_RECONCILED: 'listing checked against the manifest',
  SOURCE_BASELINE_RECONCILED: 'listing checked against the source itself',
};

/**
 * The stages of an analysis run, in the pipeline's own fixed order.
 *
 * The run page draws these beside the tools. Unmapped, a stage-entered event
 * announced "content walk" and "evidence gate", which name the machinery
 * rather than the work.
 */
const ANALYSIS_STAGE: Record<string, string> = {
  INVENTORY: 'listing what is there',
  CONTENT_WALK: 'reading the files',
  ATTRIBUTION: 'tracing files to software',
  RECONSTRUCTION: 'turning files into records',
  EVIDENCE_GATE: 'checking the evidence',
  VERDICT: 'writing the verdict',
};

/**
 * What a durable analysis event records.
 *
 * A lease is scheduler vocabulary. What it means to an operator is that a
 * worker picked this tool up, or picked it up again after the last one
 * stopped, so that is what the event ledger says.
 */
const ANALYSIS_EVENT_KIND: Record<string, string> = {
  RUN_QUEUED: 'run queued',
  RUN_STARTED: 'run started',
  TOOL_LEASE_ACQUIRED: 'a worker took this tool',
  TOOL_LEASE_RECOVERED: 'taken again after a worker stopped',
  TOOL_COMPLETED: 'tool finished',
  RUN_COMPLETED: 'run finished',
  RUN_STAGE_ENTERED: 'stage started',
};


/**
 * Whether a bucket's contents can be reached, and how far the walk got.
 *
 * PENDING sits on essentially every bucket, including ones that walk and seal
 * perfectly well, so none of these may be read as a diagnosis. They are said
 * plainly and nothing gates on them.
 */
const ACCESS_STATE: Record<string, string> = {
  ACCESSIBLE: 'readable',
  PARTIAL: 'partly readable',
  ACCESS_DENIED: 'permission refused',
  KMS_DENIED: 'encryption key refused',
  UNKNOWN: 'not established',
};

/**
 * How a walker rule recognised a file.
 *
 * These reach the attribution ledger, where an operator is asking what
 * evidence stands behind an assignment. "magic bytes" and "native id grammar"
 * are the rule's own words for it.
 */
const WALKER_RULE_METHOD: Record<string, string> = {
  MAGIC_BYTES: 'the first bytes of the file',
  SCHEMA_FINGERPRINT: 'the shape of its fields',
  NATIVE_ID_GRAMMAR: 'the form of the ids inside it',
  VENDOR_MANIFEST: 'a manifest the tool writes',
  CROSS_REFERENCE: 'agreement with other files',
  CONTAINER_INHERITANCE: 'the archive it came in',
};

/** The kinds of evidence a tool detection can rest on. */
const EVIDENCE_FAMILY: Record<string, string> = {
  S3_OBJECT_METADATA: 'what the storage records about the file',
  PATH_NAMESPACE: 'where it sits in the tree',
  MIME_MAGIC: 'the first bytes of the file',
  ARCHIVE_STRUCTURE: 'how its archive is laid out',
  VENDOR_MANIFEST: 'a manifest the tool writes',
  SCHEMA_SIGNATURE: 'the shape of its fields',
  NATIVE_IDENTIFIER: 'the form of the ids inside it',
  CROSS_FILE_CONSISTENCY: 'agreement across files',
  REFERENTIAL_INTEGRITY: 'references that resolve',
  TEMPORAL_DISTRIBUTION: 'how its dates are spread',
  SOURCE_VALIDATOR: 'a check the source itself provides',
  DETERMINISTIC_PARSER: 'a parser that reads it exactly',
  REPRESENTATIVE_RECONSTRUCTION: 'a rebuilt record that matches',
  OFFICIAL_DOCUMENTATION: 'the vendor documenting this format',
  CHECKSUM_OR_AUTHORITATIVE_COUNT: 'a checksum or a count that agrees',
};

/** Whether a reconstructed record survived its own checks. */
const RECORD_INTEGRITY: Record<string, string> = {
  VALIDATED: 'checked and sound',
  WARNING: 'checked, with warnings',
  BROKEN: 'failed its checks',
  NOT_TESTED: 'not checked',
  // Shared with a decision's confidence, deliberately: one token, one
  // meaning. The collision guard fails the build if the two ever drift.
  INDETERMINATE: 'could not be decided',
};

/** Where a decision stands, and how far its evidence got. */
const DECISION_STATUS: Record<string, string> = {
  CURRENT: 'in force',
  REJECTED: 'refused by the evidence gate',
  VERIFIED: 'verified',
};

/**
 * The maps, kept separately addressable so a test can prove two of them never
 * disagree about the same token. `ALL` is a single flat namespace keyed on
 * bare enum values, so a key defined twice is silently won by whichever map
 * spreads last, and the losing surface then renders the other contract's
 * wording with nothing failing anywhere.
 */
export const VOCABULARIES: Record<string, Record<string, string>> = {};

const ALL: Record<string, string> = {
  ...STRAY_REASON,
  ...GAP_CLASS,
  ...PACKAGING_STATE,
  ...RUN_STATE,
  ...REPORT_UNAVAILABLE,
  ...INVENTORY_COVERAGE,
  ...ANALYSIS_STAGE,
  ...ANALYSIS_EVENT_KIND,
  ...ACCESS_STATE,
  ...WALKER_RULE_METHOD,
  ...EVIDENCE_FAMILY,
  ...RECORD_INTEGRITY,
  ...DECISION_STATUS,
  ...SECTION_REASON,
  ...DELIVERY_STATE,
  ...RECONSTRUCTION_REQUEST_STATE,
};

Object.assign(VOCABULARIES, {
  STRAY_REASON,
  GAP_CLASS,
  PACKAGING_STATE,
  RUN_STATE,
  REPORT_UNAVAILABLE,
  INVENTORY_COVERAGE,
  ANALYSIS_STAGE,
  ANALYSIS_EVENT_KIND,
  ACCESS_STATE,
  WALKER_RULE_METHOD,
  EVIDENCE_FAMILY,
  RECORD_INTEGRITY,
  DECISION_STATUS,
  SECTION_REASON,
  DELIVERY_STATE,
  RECONSTRUCTION_REQUEST_STATE,
});

/** Every token this module can translate. Exported for the coverage guard. */
export const TRANSLATED: Readonly<Record<string, string>> = ALL;

/** A backend enum in plain English, or the term itself made readable. */
export function say(value: string | null | undefined): string {
  if (!value) return '';
  return ALL[value] ?? value.toLowerCase().replace(/_/g, ' ');
}

/** Field names the API uses, as an operator would name them. */
const FIELD: Record<string, string> = {
  objects: 'files',
  objectsTotal: 'files',
  objectsUploaded: 'files sent',
  objectsVerified: 'files verified',
  bytes: 'size',
  bytesTotal: 'total size',
  byteTotal: 'total size',
  bytesUploaded: 'sent',
  byteLength: 'size',
  deterministicObjects: 'matched by rule',
  humanConfirmedObjects: 'confirmed by a person',
  inventoryTotal: 'files listed',
  attributed: 'attributed to a tool',
  stray: 'not attributed',
  reconciled: 'adds up',
  sealedAt: 'sealed',
  requestedAt: 'requested',
  completedAt: 'completed',
  stateChangedAt: 'last changed',
  occurredAt: 'happened',
  decidedAt: 'decided',
  bucketName: 'bucket',
  packageTitle: 'title',
  toolId: 'tool',
  label: 'tool',
  kind: 'type',
  state: 'status',
  reason: 'why',
  basis: 'basis',
  pathSummary: 'what it is',
  files: 'files',
  proposal: 'suggested',
  decision: 'decided',
  open: 'open',
  blockingOpen: 'blocking',
  acknowledged: 'acknowledged',
  resolved: 'resolved',
  axisOptions: 'layout options',
  deliveryRegion: 'region',
  region: 'region',
  fileName: 'file',
  version: 'version',
};

export function fieldName(key: string): string {
  if (FIELD[key]) return FIELD[key];
  return key
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/_/g, ' ')
    .toLowerCase();
}

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const SHA256 = /^[0-9a-f]{64}$/i;
const ISO = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/;

/** True for values that identify a row rather than tell an operator anything. */
export function isIdentifier(key: string, value: unknown): boolean {
  if (typeof value !== 'string') return false;
  if (UUID.test(value) || SHA256.test(value)) return true;
  return /(^|[a-z])(id|Id|sha256|Sha256|digest|key|Key)$/.test(key) && value.length > 24;
}

/** A value an operator can read: dates local, sizes human, enums in English. */
export function readable(key: string, value: unknown): string {
  if (value === null || value === undefined) return '—';
  if (typeof value === 'boolean') return value ? 'yes' : 'no';
  if (typeof value === 'number') {
    if (/bytes|byteLength|size/i.test(key)) return humanBytes(value);
    return value.toLocaleString();
  }
  if (typeof value !== 'string') return String(value);
  if (ISO.test(value)) {
    const d = new Date(value);
    if (!Number.isNaN(d.getTime()))
      return d.toLocaleString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
  }
  if (/^[A-Z][A-Z0-9_]{2,}$/.test(value)) return say(value);
  return value;
}

function humanBytes(n: number): string {
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  let v = n;
  let i = 0;
  while (v >= 1024 && i < units.length - 1) {
    v /= 1024;
    i += 1;
  }
  return `${v >= 100 || i === 0 ? Math.round(v) : v.toFixed(1)} ${units[i]}`;
}


/**
 * What each product capability means to the person using it, and what its
 * absence costs them. The Operations page rendered the raw gate names, which
 * told an operator nothing about what they can or cannot do today.
 *
 * Written so the unavailable ones read as a missing ability rather than a
 * failed flag: a buyer reading this should learn the shape of the product,
 * not the shape of the enum.
 */
/**
 * What an absent capability is waiting for.
 *
 * These are not independent gaps. Everything keys off which tool made a
 * file, so the absences share three causes and resolve in one order. Listing
 * them flat implied twelve separate pieces of work, which reads as a product
 * that is nowhere near ready rather than one chain whose bottom link started
 * working today.
 */
export type Blocker = 'RECONSTRUCTION' | 'PROMOTION' | 'EXECUTOR' | 'NONE';

export const BLOCKER: Record<Blocker, { title: string; why: string }> = {
  RECONSTRUCTION: {
    title: 'Waiting on reconstruction',
    why: 'These read from reconstructed records - messages, mailboxes, channels, people. The tables for those are fully modelled and nothing has ever written to them. They all wait on the same one, so they open together.',
  },
  PROMOTION: {
    title: 'Waiting on proven tools',
    why: 'A tool is only proven with ten distinct objects across five evidence methods, and the method is fixed by the rule that matched. Until archives are expanded there are not five methods in play, so no tool is promoted.',
  },
  EXECUTOR: {
    title: 'Waiting on a worker',
    why: 'The decision, the checks and the record all exist. What is missing is the process that does the work.',
  },
  NONE: { title: 'Buildable now', why: 'Nothing in the chain blocks this one.' },
};


export const CAPABILITY_ORDER = [
  'Attribution works end to end on real data',
  'Archive expansion, which unlocks the evidence methods promotion needs',
  'Reconstruction - the missing producer',
  'Then graph, preview, search, chat and delivery, mostly as read surfaces over facts that finally exist',
];

export const CAPABILITY: Record<string, { name: string; does: string; waitsOn?: Blocker }> = {
  sourceCatalog: { name: 'Bucket registration', does: 'point the tool at any S3 bucket in the account' },
  companyCatalog: { name: 'Companies', does: 'group buckets under the company they belong to' },
  toolInventory: { name: 'Software detected', does: 'see which software produced the files in a bucket' },
  attribution: { name: 'File attribution', does: 'trace each individual file to the software that made it' },
  analysis: { name: 'Company analysis', does: 'run every detected tool across a company and record the result' },
  sync: { name: 'Bucket listing', does: 'observe what is in a bucket, and what changed since last time' },
  packaging: { name: 'Packaging', does: 'assemble a named, documented, checked package ready to hand over' },
  delivery: { name: 'Delivery', does: 'copy the finished package into the buyer\u2019s own bucket', waitsOn: 'EXECUTOR' },
  reconstruction: { name: 'Reconstruction', does: 'turn files into records - messages, mailboxes, channels, people' , waitsOn: 'RECONSTRUCTION' },
  preview: { name: 'Record preview', does: 'open a reconstructed record and read it' , waitsOn: 'RECONSTRUCTION' },
  reporting: { name: 'Reporting', does: 'gap findings and readiness verdicts per bucket' , waitsOn: 'RECONSTRUCTION' },
  recency: { name: 'Recency', does: 'show how current the data is, not just how much there is' , waitsOn: 'RECONSTRUCTION' },
  liveness: { name: 'Liveness', does: 'prove the source is still reachable and still producing' , waitsOn: 'RECONSTRUCTION' },
  commercialReadiness: { name: 'Commercial verdict', does: 'state whether a corpus is fit to sell, with the evidence' , waitsOn: 'RECONSTRUCTION' },
  graph: { name: 'Relationship graph', does: 'walk the connections between reconstructed records' , waitsOn: 'RECONSTRUCTION' },
  walkthrough: { name: 'Browse the records', does: 'open a bucket tool by tool and read the actual rows' , waitsOn: 'PROMOTION' },
  search: { name: 'Search', does: 'find a record across a bucket by what it says' , waitsOn: 'RECONSTRUCTION' },
  chat: { name: 'Ask the corpus', does: 'ask questions and get answers with citations back to files' , waitsOn: 'RECONSTRUCTION' },
  operations: { name: 'Operations view', does: 'watch every run across every bucket in one place' , waitsOn: 'NONE' },
};


/** The three substantive sections of an analysis report, named for a reader. */
export const REPORT_SECTION: Record<string, { title: string; does: string }> = {
  attribution: { title: 'Attribution', does: 'which software produced each file' },
  entityReconstruction: { title: 'Reconstructed records', does: 'files turned into messages, mailboxes, channels and people' },
  commercialVerdict: { title: 'Commercial verdict', does: 'whether this corpus is fit to sell, with the evidence' },
};
